import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, GiftIcon, XIcon } from 'lucide-react';
import { CDV_ANNUAL_URL, CDV_MONTHLY_URL } from '../../data/academiaCdv';
import { trackPage } from '../../utils/analytics';
interface CdvExitModalProps {
  open: boolean;
  onClose: () => void;
}
export function CdvExitModal({
  open,
  onClose
}: CdvExitModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    trackPage('cdv_exit_popup_shown');
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-33" data-mp="1ox7fokmf9q3e">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 px-3 py-4 md:px-4" onClick={onClose} data-id="element-34" data-mp="1oxyy0tr0drbq">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="cdv-exit-title" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-white/10 bg-[#0B0B0B] p-5 pt-10 md:p-8 md:pt-10 text-white shadow-[0_20px_60px_rgba(0,0,0,0.6)]" data-id="element-35" data-mp="3pmop9irb86mx">
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-white/60 transition-colors duration-150 ease-out hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-metallic" data-id="element-36" data-mp="q606i80pukgz">
              <XIcon className="h-5 w-5" data-id="element-37" data-mp="jooy98f1mte4" />
            </button>

            <div className="text-center" data-id="element-38" data-mp="jrbd7t49kb6d">
              <p className="text-sm font-bold uppercase tracking-wider text-gold-metallic" data-id="element-39" data-mp="ergq80nm05ql">
                ¡Antes de que te vayas!
              </p>
              <h2 id="cdv-exit-title" className="mt-2 font-sans text-2xl md:text-3xl font-extrabold uppercase leading-tight" data-id="element-40" data-mp="1cg06j5z2vc7k">
                Bloquea y aprovecha la oferta ahora
              </h2>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-5 items-stretch" data-id="element-41" data-mp="xohwdoswgbw4">
              {/* Plan Mensual */}
              <div className="order-2 md:order-1 md:col-span-2 flex flex-col rounded-2xl border border-white/15 bg-white/[0.02] p-5 md:p-6" data-id="element-42" data-mp="1v6qbd087l932">
                <p className="text-sm font-bold uppercase tracking-wider text-white/70" data-id="element-43" data-mp="1yn2vwl0l0ffq">
                  Plan Mensual
                </p>
                <p className="mt-3 flex items-baseline gap-2" data-id="element-44" data-mp="22oog6rfg3vzy">
                  <span className="font-sans text-4xl md:text-5xl font-extrabold text-white" data-id="element-45" data-mp="27mzrz7pawgkt">
                    $47
                  </span>
                  <span className="text-base text-white/60" data-id="element-46" data-mp="2cnnils9mavei">USD / mes</span>
                </p>
                <p className="mt-2 text-sm text-white/70" data-id="element-47" data-mp="2hl6u4kexku2r">
                  Conservas este precio mientras sigas dentro. Cancela cuando
                  quieras.
                </p>
                <div className="mt-auto pt-6" data-id="element-48" data-mp="2qf1yhq88aghc">
                  <a href={CDV_MONTHLY_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackPage('cdv_exit_mensual_click')} className="flex w-full items-center justify-center rounded-xl border-2 border-white/30 px-4 py-3.5 text-center text-sm font-bold uppercase text-white transition-colors duration-150 ease-out hover:border-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-49" data-mp="2vcu23hi8jr1d">
                    Quiero el Plan Mensual
                  </a>
                </div>
              </div>

              {/* Plan Anual */}
              <div className="order-1 md:order-2 md:col-span-3 relative flex flex-col rounded-2xl border-2 border-gold-metallic bg-[#1C1710] p-5 md:p-6 shadow-[0_12px_40px_rgba(212,175,55,0.18)]" data-id="element-50" data-mp="3j3isnmyg0p4w">
                <span className="absolute -top-3.5 left-5 whitespace-nowrap rounded-full bg-gold-metallic px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-black" data-id="element-51" data-mp="3s48lqiwbk1ex">
                  Mejor valor · Ahorras más del 50%
                </span>
                <p className="text-sm font-bold uppercase tracking-wider text-gold-metallic" data-id="element-52" data-mp="17cewr674ikdi">
                  Plan Premium Anual
                </p>
                <p className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-53" data-mp="1aw70o607dlxe">
                  <span className="font-sans text-5xl md:text-6xl font-extrabold text-white" data-id="element-54" data-mp="1ay9xb8vv2db3">
                    $247
                  </span>
                  <span className="text-base text-white/70" data-id="element-55" data-mp="10ysxh83aitn1">USD / año</span>
                  <span className="text-base text-white/45 line-through" data-id="element-56" data-mp="15wavi3frrog9">
                    $797
                  </span>
                </p>
                <ul className="mt-4 space-y-2 text-sm md:text-base text-white/85" data-id="element-57" data-mp="1tmx2dre1f2oo">
                  <li className="flex gap-2.5" data-id="element-58" data-mp="1ykgjr0617vku">
                    <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-metallic" aria-hidden="true" data-id="element-59" data-mp="1ynciurtm8qav" />
                    Todo desbloqueado desde el primer día
                  </li>
                  <li className="flex gap-2.5" data-id="element-60" data-mp="2ckhjudeh2yz9">
                    <GiftIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-metallic" aria-hidden="true" data-id="element-61" data-mp="2hbzjmy4retin" />
                    <span data-id="element-62" data-mp="2r5vnd1lit8p3">
                      Incluye la{' '}
                      <strong className="text-white" data-id="element-63" data-mp="2m9y4oomsbq95">
                        Consultoría 1 a 1 de IA
                      </strong>
                    </span>
                  </li>
                </ul>
                <div className="mt-auto pt-6" data-id="element-64" data-mp="2d7f5qfm4dxs6">
                  <a href={CDV_ANNUAL_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackPage('cdv_exit_anual_click')} className="flex w-full items-center justify-center rounded-xl bg-gold-metallic px-4 py-4 text-center text-sm md:text-base font-extrabold uppercase text-black shadow-lg transition-[background-color,transform] duration-150 ease-out hover:bg-[#E5C04B] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1710]" data-id="element-65" data-mp="2893wa5zqlpah">
                    Quiero el Plan Anual con descuento
                  </a>
                </div>
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-white/50" data-id="element-66" data-mp="52eo1vnhivam">
              Las inscripciones cierran el Lunes 12 de Octubre, 11:59 PM (CDMX)
            </p>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}